import { EventBusy, KeyboardArrowUp } from '@mui/icons-material';
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import { CalendarEvent } from '../../../global/interfaces';
import { IBooking } from '@homeporter/types';
import SessionSummaryModal from '../SessionSummaryModal';

interface EventTableProps {
  groupedEvents: { [key: string]: CalendarEvent[] } | [];
  filters: boolean;
}

const EventTable = ({ groupedEvents, filters }: EventTableProps) => {
  const {
    getIconStyle,
    selectedBooking,
    setSelectedBooking,
    setIsSessionSummaryModalOpen,
    isSessionSummaryModalOpen,
  } = useBookingsContext();

  type SortableKeys =
    | 'booking_date'
    | 'start'
    | 'description'
    | 'title'
    | 'session_title';

  const [sortConfig, setSortConfig] = useState<{
    key: SortableKeys;
    direction: 'ascending' | 'descending';
  }>({
    key: 'start',
    direction: 'ascending',
  });

  const handleSort = (key: SortableKeys) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortConfig({ key, direction });
  };

  const sortedGroupedEvents = useMemo(() => {
    const allEvents = Object.entries(groupedEvents).flatMap(([day, sessions]) =>
      sessions.map((session) => ({
        ...session,
        day,
      })),
    );

    const extractValue = (
      item: any,
      key: string,
    ): string | number | undefined => {
      if (key === 'start' || key === 'booking_date') {
        const dateValue =
          key === 'start' ? item.start : item.details?.booking_date;
        return dateValue ? new Date(dateValue).getTime() : 0;
      }

      if (['title', 'session_title', 'description'].includes(key)) {
        const detailsValue = item.details?.[key as keyof IBooking];
        const directValue = item[key as keyof CalendarEvent];
        return (detailsValue ?? directValue ?? '').toString().toLowerCase();
      }

      return (item[key as keyof CalendarEvent] ?? '').toString().toLowerCase();
    };

    const sortedEvents = allEvents.sort((a, b): any => {
      const { key, direction } = sortConfig;

      const aValue = extractValue(a, key);
      const bValue = extractValue(b, key);
      if (aValue && bValue) {
        if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    return sortedEvents.reduce(
      (acc, session) => {
        acc[session.day] = acc[session.day] || [];
        acc[session.day].push(session);
        return acc;
      },
      {} as Record<string, CalendarEvent[]>,
    );
  }, [groupedEvents, sortConfig]);

  const handleEventClick = (event: CalendarEvent) => {
    console.log(event);
    setSelectedBooking(event.details);
    setIsSessionSummaryModalOpen(true);
    console.log(groupedEvents);
  };

  return (
    <>
      {Object.entries(groupedEvents).length > 0 ? (
        <>
          {filters ? (
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: '500',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort('booking_date')}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    Date{' '}
                    <KeyboardArrowUp
                      sx={{
                        transform:
                          sortConfig.key === 'booking_date'
                            ? sortConfig.direction === 'ascending'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)'
                            : 'rotate(0deg)',
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: '500',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort('start')}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    Time{' '}
                    <KeyboardArrowUp
                      sx={{
                        transform:
                          sortConfig.key === 'start'
                            ? sortConfig.direction === 'ascending'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)'
                            : 'rotate(0deg)',
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: '500',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort('description')}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    Description{' '}
                    <KeyboardArrowUp
                      sx={{
                        transform:
                          sortConfig.key === 'description'
                            ? sortConfig.direction === 'ascending'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)'
                            : 'rotate(0deg)',
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: '500',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort('session_title')}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    Session Type{' '}
                    <KeyboardArrowUp
                      sx={{
                        transform:
                          sortConfig.key === 'session_title'
                            ? sortConfig.direction === 'ascending'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)'
                            : 'rotate(0deg)',
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: '500',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort('title')}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    Title{' '}
                    <KeyboardArrowUp
                      sx={{
                        transform:
                          sortConfig.key === 'title'
                            ? sortConfig.direction === 'ascending'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)'
                            : 'rotate(0deg)',
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {Object.entries(
              sortedGroupedEvents as Record<string, CalendarEvent[]>,
            ).map(([day, sessions]: [string, CalendarEvent[]]) => (
              <React.Fragment key={day}>
                <TableRow sx={{ '&:hover': { backgroundColor: '#ebebeb' } }}>
                  <TableCell
                    onClick={() => handleEventClick(sessions[0])}
                    rowSpan={sessions.length}
                    align="center"
                    sx={{
                      width: '20%',
                      fontSize: '1rem',
                      verticalAlign: 'top',
                      color: '#464e5f',
                      backgroundColor: '#fff',
                      borderBottom: '0px',
                    }}>
                    {format(new Date(day), 'dd EEE')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleEventClick(sessions[0])}
                    sx={{ width: '15%', borderBottom: 'none !important' }}
                    align="center">
                    <Box display={'flex'} alignItems={'center'}>
                      <Box sx={getIconStyle(sessions[0].title)}></Box>
                      {`${format(new Date(sessions[0].start), 'h:mm a')} - ${format(new Date(sessions[0].end), 'h:mm a')}`}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleEventClick(sessions[0])}
                    sx={{ width: '25%', borderBottom: 'none !important' }}
                    align="center">
                    {sessions[0].details?.description
                      ? sessions[0].details?.description?.length > 40
                        ? `${sessions[0].details?.description.slice(0, 40)}...`
                        : sessions[0].details?.description
                      : 'No Description'}
                  </TableCell>
                  <TableCell
                    onClick={() => handleEventClick(sessions[0])}
                    sx={{ width: '20%', borderBottom: 'none !important' }}
                    align="center">
                    {sessions[0].title}
                  </TableCell>
                  <TableCell
                    onClick={() => handleEventClick(sessions[0])}
                    sx={{ width: '20%', borderBottom: 'none !important' }}
                    align="center">
                    {sessions[0]?.details?.title}
                  </TableCell>
                </TableRow>
                {sessions.slice(1).map((session, index) => (
                  <TableRow
                    onClick={() => handleEventClick(session)}
                    key={`${day}-${index}`}
                    sx={{ '&:hover': { backgroundColor: '#ebebeb' } }}>
                    <TableCell
                      onClick={() => handleEventClick(session)}
                      sx={{ width: '15%', borderBottom: 'none' }}
                      align="center">
                      <Box display={'flex'} alignItems={'center'}>
                        {session?.details?.title && (
                          <Box sx={getIconStyle(session?.title)}></Box>
                        )}
                        {`${format(new Date(session.start), 'h:mm a')} - ${format(new Date(session.end), 'h:mm a')}`}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleEventClick(session)}
                      sx={{ width: '25%', borderBottom: 'none' }}
                      align="center">
                      {session.details?.description
                        ? session.details?.description?.length > 40
                          ? `${session.details?.description.slice(0, 40)}...`
                          : session.details?.description
                        : 'No Description'}
                    </TableCell>
                    <TableCell
                      onClick={() => handleEventClick(session)}
                      sx={{ width: '20%', borderBottom: 'none !important' }}
                      align="center">
                      {session.title}
                    </TableCell>
                    <TableCell
                      onClick={() => handleEventClick(session)}
                      sx={{ width: '20%', borderBottom: 'none' }}
                      align="center">
                      {session.details?.title}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ borderBottom: 'solid 1px #e6e6e6' }}>
                  <TableCell colSpan={5} sx={{ padding: 0 }} />
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </>
      ) : (
        <Box>
          <Typography
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            height={'10rem'}>
            <EventBusy sx={{ marginRight: '1rem' }} /> No Sessions To Display
          </Typography>
        </Box>
      )}
      <SessionSummaryModal
        open={isSessionSummaryModalOpen}
        onClose={() => setIsSessionSummaryModalOpen(false)}
        details={selectedBooking}
      />
    </>
  );
};
export default EventTable;
